import * as React from 'react';
import { useState } from 'react';
import { Box, Container, Typography, Grid, Link, Slider, useTheme } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import { CurrencyRupee } from '@mui/icons-material';

const PatternOverlay = (theme) => ({
    position: 'absolute',
    inset: 0,
    backgroundImage: "url(\"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gMTAwIDAgTCAwIDAgMCAxMDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIyZDNlZSIgc3Ryb2tlLXdpZHRoPSIwLjUiIG9wYWNpdHk9IjAuMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==\")",
    opacity: 0.3,
    zIndex: 0,
});

export default function ROICalculator() {
    const theme = useTheme();

    const [monthlyDefects, setMonthlyDefects] = useState(10);
    const [avgCostPerDefect, setAvgCostPerDefect] = useState(1000);
    const [teamSize, setTeamSize] = useState(5);

    const costReductionFactor = 0.87;
    const currentMonthlyCost = monthlyDefects * avgCostPerDefect;
    const currentYearlyCost = currentMonthlyCost * 12;
    const annualNCProCost = currentYearlyCost * 0.02;
    const savings = currentYearlyCost * costReductionFactor;

    const timeCurrently = teamSize * 160 * 0.4;
    const timeWithNCPro = teamSize * 160 * 0.05;
    const timeSaved = timeCurrently - timeWithNCPro;

    const roiMultiplier = savings / annualNCProCost;

    const amber400 = '#FB923C';
    const orange400 = '#FDBA74';
    const red400 = '#F87171';
    const green500 = '#10B981';
    const emerald600 = '#059669';
    const cyan100 = '#CFFAFE';
    const red300 = '#FCA5A5';

    const sliderSx = {
        padding: '13px 0',
        '& .MuiSlider-track': {
            backgroundColor: 'transparent',
            border: 'none',
            height: 8,
        },
        '& .MuiSlider-rail': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            height: 8,
            borderRadius: 4,
        },
        '& .MuiSlider-thumb': {
            color: amber400,
            width: 20,
            height: 20,
        },
        '& .MuiSlider-root': {
            height: 8,
        }
    };


    return (
        <Box
            component="section"
            id="roi"
            sx={{
                py: { xs: 8, md: 12 },
                background: 'linear-gradient(to bottom right, #1F2937, #1E3A8A, #083344)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box sx={PatternOverlay(theme)} />

            <Container
                maxWidth="lg"
                sx={{
                    px: { xs: 2, sm: 3 },
                    position: 'relative',
                    zIndex: 10,
                }}
            >
                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 8, md: 10 } }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            background: `linear-gradient(to right, ${amber400}, ${orange400})`,
                            px: 2,
                            py: 1,
                            borderRadius: 999,
                            mb: 3,
                        }}
                    >
                        <CalculateIcon sx={{ width: 16, height: 16, color: 'white' }} />
                        <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'white' }}>
                            ROI Calculator
                        </Typography>
                    </Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '3rem' },
                            fontWeight: 700,
                            mb: 3,
                            lineHeight: 1.1,
                        }}
                    >
                        How Much Are{' '}
                        <Box component="span" sx={{
                            background: `linear-gradient(to right, ${red400}, ${orange400})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                        }}>
                            Quality Issues
                        </Box>{' '}
                        Costing You?
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.25rem',
                            color: cyan100,
                        }}
                    >
                        Calculate your potential savings with NC Pro in under 60 seconds
                    </Typography>
                </Box>


                <Grid
                    container
                    spacing={{ xs: 3, md: 6 }}
                    sx={{ width: '100%' }}
                >
                    <Grid item xs={12} lg={6}>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: 3,
                                p: { xs: 2.5, sm: 4 },
                                boxSizing: 'border-box',
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                            }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CalculateIcon sx={{ width: 28, height: 28, color: amber400 }} />
                                Your Current Situation
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: cyan100, mb: 1.5 }}>
                                        Average Quality Issues Per Month
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Slider
                                            value={monthlyDefects}
                                            onChange={(_e, newValue) => setMonthlyDefects(newValue)}
                                            min={10}
                                            max={200}
                                            sx={sliderSx}
                                        />
                                        <Box
                                            sx={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                backdropFilter: 'blur(4px)',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                minWidth: 80,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {monthlyDefects}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: cyan100, mb: 1.5 }}>
                                        Average Cost Per Issue (Rework + Complaints + Lost Time)
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Slider
                                            value={avgCostPerDefect}
                                            onChange={(_e, newValue) => setAvgCostPerDefect(newValue)}
                                            min={1000}
                                            max={50000}
                                            step={1000}
                                            sx={sliderSx}
                                        />
                                        <Box
                                            sx={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                backdropFilter: 'blur(4px)',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                minWidth: 100,
                                                textAlign: 'center',
                                            }}
                                        >
                                            ₹{avgCostPerDefect.toLocaleString()}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: cyan100, mb: 1.5 }}>
                                        Quality Team Size
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Slider
                                            value={teamSize}
                                            onChange={(_e, newValue) => setTeamSize(newValue)}
                                            min={2}
                                            max={50}
                                            sx={sliderSx}
                                        />
                                        <Box
                                            sx={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                backdropFilter: 'blur(4px)',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                minWidth: 80,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {teamSize} people
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                        backdropFilter: 'blur(4px)',
                                        borderRadius: 3,
                                        p: 3,
                                        border: '2px solid rgba(248, 113, 113, 0.3)',
                                        mt: 3,
                                    }}
                                >


                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                        <WarningIcon sx={{ width: 24, height: 24, color: red300 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#FECACA' }}>
                                            Current Annual Cost
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" sx={{ fontWeight: 700, color: red300, mb: 1 }}>
                                        ₹{(currentYearlyCost / 100000).toFixed(2)}L
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#FCA5A5' }}>
                                        Your team spends {timeCurrently.toFixed(0)} hours/month on quality issues
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                            <Box
                                sx={{
                                    background: `linear-gradient(to bottom right, ${green500}, ${emerald600})`,
                                    borderRadius: 3,
                                    p: { xs: 2.5, sm: 4 },
                                    boxSizing: 'border-box',
                                    boxShadow: theme.shadows[15],
                                }}
                            >   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CurrencyRupee sx={{ width: 28, height: 28, color: 'white' }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                                        With NC Pro
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ color: '#A7F3D0', fontWeight: 600, mb: 0.5 }}>
                                        Annual Savings
                                    </Typography>
                                    <Typography variant="h2" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                                        ₹{(savings / 100000).toFixed(2)}L
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#A7F3D0' }}>   <TrendingUpIcon sx={{ width: 20, height: 20 }} />
                                        <Typography component="span" sx={{ fontWeight: 600 }}>
                                            87% cost reduction
                                        </Typography>
                                    </Box>
                                </Box>

                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                backdropFilter: 'blur(4px)',
                                                borderRadius: 1,
                                                p: 2,
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: '#A7F3D0', mb: 0.5, display: 'block' }}>Monthly Savings</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                                                ₹{(savings / 12 / 1000).toFixed(0)}K
                                            </Typography>
                                        </Box>
                                    </Grid>  <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                backdropFilter: 'blur(4px)',
                                                borderRadius: 1,
                                                p: 2,
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: '#A7F3D0', mb: 0.5, display: 'block' }}>Time Saved</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                                                {timeSaved.toFixed(0)}hrs
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Box
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(4px)',
                                        borderRadius: 1,
                                        p: 2,
                                        mb: 3,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: '#A7F3D0', mb: 1, fontWeight: 600 }}>Return on Investment</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                                        {roiMultiplier.toFixed(0)}X ROI
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#A7F3D0' }}>
                                        NC Pro pays for itself in less than 2 weeks
                                    </Typography>
                                </Box><Link
                                    href="#demo"
                                    underline="none"
                                    sx={{
                                        display: 'block',
                                        width: '100%',
                                        backgroundColor: 'white',
                                        color: '#059669',
                                        px: 4,
                                        py: 2,
                                        borderRadius: 3,
                                        fontWeight: 700,
                                        fontSize: '1.125rem',
                                        textAlign: 'center',
                                        transition: 'all 0.3s',
                                        boxShadow: theme.shadows[6],
                                        '&:hover': {
                                            backgroundColor: theme.palette.grey[100],
                                            boxShadow: theme.shadows[10],
                                        },
                                    }}
                                >
                                    See How We Can Save You ₹{(savings / 100000).toFixed(2)}L
                                </Link>
                            </Box> <Box
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(8px)',
                                    borderRadius: 3,
                                    p: 3,
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>What You Get:</Typography>
                                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {[
                                        'AI-powered root cause analysis in minutes, not days',
                                        'Automated 8D workflow reduces manual work by 85%',
                                        'One-click reports for customers and auditors',
                                        'Stop recurring defects with predictive insights'
                                    ].map((benefit, index) => (
                                        <Box component="li" key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    backgroundColor: green500,
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    mt: 0.5,
                                                }}
                                            >
                                                <Typography component="span" sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 700 }}>
                                                    ✓
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ color: cyan100 }}>{benefit}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
